'use client';

import { useState, useRef } from 'react';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import * as Popover from '@radix-ui/react-popover';

interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  label?: string;
}

// 256-color palette (web-safe colors + extended palette)
const PREDEFINED_COLORS = [
  // Grayscale (16 colors)
  '#000000',
  '#111111',
  '#222222',
  '#333333',
  '#444444',
  '#555555',
  '#666666',
  '#777777',
  '#888888',
  '#999999',
  '#AAAAAA',
  '#BBBBBB',
  '#CCCCCC',
  '#DDDDDD',
  '#EEEEEE',
  '#FFFFFF',

  // Reds
  '#330000',
  '#660000',
  '#990000',
  '#CC0000',
  '#FF0000',
  '#FF3333',
  '#FF6666',
  '#FF9999',
  '#FFCCCC',
  '#800000',
  '#A52A2A',
  '#B22222',
  '#DC143C',
  '#CD5C5C',
  '#F08080',
  '#E9967A',

  // Oranges
  '#331A00',
  '#663300',
  '#994D00',
  '#CC6600',
  '#FF8000',
  '#FF9933',
  '#FFB266',
  '#FFCC99',
  '#FFE5CC',
  '#FF4500',
  '#FF6347',
  '#FF7F50',
  '#FFA07A',
  '#FFA500',
  '#FFD700',
  '#FFDAB9',

  // Yellows
  '#333300',
  '#666600',
  '#999900',
  '#CCCC00',
  '#FFFF00',
  '#FFFF33',
  '#FFFF66',
  '#FFFF99',
  '#FFFFCC',
  '#F0E68C',
  '#FAFAD2',
  '#FFEFD5',
  '#FFE4B5',
  '#FFDEAD',
  '#F5DEB3',
  '#EEE8AA',

  // Yellow-Greens
  '#1A3300',
  '#336600',
  '#4D9900',
  '#66CC00',
  '#80FF00',
  '#99FF33',
  '#B3FF66',
  '#CCFF99',
  '#E5FFCC',
  '#9ACD32',
  '#ADFF2F',
  '#7FFF00',
  '#7CFC00',
  '#00FF00',
  '#32CD32',
  '#98FB98',

  // Greens
  '#003300',
  '#006600',
  '#009900',
  '#00CC00',
  '#00FF00',
  '#33FF33',
  '#66FF66',
  '#99FF99',
  '#CCFFCC',
  '#228B22',
  '#2E8B57',
  '#3CB371',
  '#90EE90',
  '#8FBC8F',
  '#20B2AA',
  '#00FA9A',

  // Cyans
  '#003333',
  '#006666',
  '#009999',
  '#00CCCC',
  '#00FFFF',
  '#33FFFF',
  '#66FFFF',
  '#99FFFF',
  '#CCFFFF',
  '#008B8B',
  '#40E0D0',
  '#48D1CC',
  '#00CED1',
  '#5F9EA0',
  '#66CDAA',
  '#7FFFD4',

  // Blues
  '#000033',
  '#000066',
  '#000099',
  '#0000CC',
  '#0000FF',
  '#3333FF',
  '#6666FF',
  '#9999FF',
  '#CCCCFF',
  '#00008B',
  '#0000CD',
  '#191970',
  '#4169E1',
  '#4682B4',
  '#6495ED',
  '#87CEEB',

  // Blue-Purples
  '#1A0033',
  '#330066',
  '#4D0099',
  '#6600CC',
  '#8000FF',
  '#9933FF',
  '#B266FF',
  '#CC99FF',
  '#E5CCFF',
  '#483D8B',
  '#6A5ACD',
  '#7B68EE',
  '#8A2BE2',
  '#9370DB',
  '#BA55D3',
  '#DDA0DD',

  // Purples
  '#330033',
  '#660066',
  '#990099',
  '#CC00CC',
  '#FF00FF',
  '#FF33FF',
  '#FF66FF',
  '#FF99FF',
  '#FFCCFF',
  '#800080',
  '#8B008B',
  '#9932CC',
  '#A020F0',
  '#9400D3',
  '#DA70D6',
  '#EE82EE',

  // Magentas/Pinks
  '#330019',
  '#660033',
  '#99004D',
  '#CC0066',
  '#FF0080',
  '#FF3399',
  '#FF66B2',
  '#FF99CC',
  '#FFCCE5',
  '#C71585',
  '#D02090',
  '#FF1493',
  '#FF69B4',
  '#FFB6C1',
  '#FFC0CB',
  '#DB7093',

  // Browns/Earth tones
  '#4A2C2A',
  '#5C3317',
  '#6B4423',
  '#8B4513',
  '#A0522D',
  '#CD853F',
  '#D2691E',
  '#DEB887',
  '#F4A460',
  '#BC8F8F',
  '#D2B48C',
  '#C4A484',
  '#B8860B',
  '#DAA520',
  '#BDB76B',
  '#808000',

  // Additional colors to reach 256
  '#2F4F4F',
  '#556B2F',
  '#6B8E23',
  '#708090',
  '#778899',
  '#696969',
  '#A9A9A9',
  '#C0C0C0',
  '#D3D3D3',
  '#DCDCDC',
  '#F0F0F0',
  '#F5F5DC',
  '#FAF0E6',
  '#FFF0F5',
  '#FFF5EE',
  '#FFFAF0',
  '#F0FFF0',
  '#FFFFF0',
  '#F0FFFF',
  '#F5FFFA',
  '#F8F8FF',
  '#FFFAFA',
  '#FDF5E6',
  '#FFF8DC',
  '#FAEBD7',
  '#FFE4E1',
  '#FFDAB9',
  '#E6E6FA',
  '#B0E0E6',
  '#ADD8E6',
  '#87CEFA',
  '#B0C4DE',
  '#778899',
  '#C0D9D9',
  '#A7A7A7',
  '#82CFFD',
  '#50BFE6',
  '#ACE5EE',
  '#79CDCD',
  '#00868B',
  '#528B8B',
  '#2F4F4F',
  '#838B8B',
  '#7F7F7F',
  '#8DB6CD',
  '#607B8B',
  '#6E7B8B',
  '#36648B',
];

export default function ColorPicker({ value, onChange, label }: ColorPickerProps) {
  const { darkMode } = useDarkMode();
  const [customColor, setCustomColor] = useState(value || '#000000');
  const [isOpen, setIsOpen] = useState(false);
  const colorInputRef = useRef<HTMLInputElement>(null);

  // Re-sync when the value prop changes (adjust-state-during-render pattern,
  // https://react.dev/learn/you-might-not-need-an-effect)
  const [prevValue, setPrevValue] = useState(value);
  if (prevValue !== value) {
    setPrevValue(value);
    if (value) {
      setCustomColor(value);
    }
  }

  const handleColorSelect = (color: string) => {
    setCustomColor(color);
    onChange(color);
    setIsOpen(false);
  };

  const handleCustomColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = e.target.value;
    setCustomColor(newColor);
    onChange(newColor);
  };

  return (
    <div className="w-full">
      {label && (
        <label
          className={`mb-1 block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
        >
          {label}
        </label>
      )}
      <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
        <Popover.Trigger asChild>
          <button
            type="button"
            className={`flex h-9 w-full items-center gap-2 rounded-md border px-2 ${
              darkMode
                ? 'border-gray-600 bg-gray-800 hover:bg-gray-700'
                : 'border-gray-300 bg-white hover:bg-gray-50'
            }`}
          >
            <div
              className="h-7 w-7 flex-shrink-0 rounded shadow-sm"
              style={{
                backgroundColor: value || '#000000',
              }}
            />
            <span
              className={`truncate font-mono text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
            >
              {value || '#000000'}
            </span>
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            className={`z-50 rounded-lg p-4 shadow-xl ${
              darkMode ? 'border border-gray-700 bg-gray-800' : 'border border-gray-200 bg-white'
            }`}
            sideOffset={5}
            align="start"
          >
            <div className="w-[320px]">
              {/* Color grid */}
              <div className="mb-3 grid max-h-[200px] grid-cols-16 gap-0.5 overflow-y-auto">
                {PREDEFINED_COLORS.map((color, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleColorSelect(color)}
                    className={`h-4 w-4 rounded-sm border transition-transform hover:z-10 hover:scale-125 ${
                      value === color ? 'ring-2 ring-blue-500 ring-offset-1' : ''
                    }`}
                    style={{
                      backgroundColor: color,
                      borderColor: darkMode ? '#374151' : '#E5E7EB',
                    }}
                    title={color}
                  />
                ))}
              </div>

              {/* Custom color picker */}
              <div className={`border-t pt-3 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <label
                  className={`mb-2 block text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
                >
                  Custom Color
                </label>
                <div className="flex items-center gap-2">
                  <input
                    ref={colorInputRef}
                    type="color"
                    value={customColor}
                    onChange={handleCustomColorChange}
                    className="h-10 w-10 cursor-pointer rounded border-0 p-0"
                  />
                  <input
                    type="text"
                    value={customColor}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (/^#[0-9A-Fa-f]{0,6}$/.test(val) || val === '') {
                        setCustomColor(val);
                        if (/^#[0-9A-Fa-f]{6}$/.test(val)) {
                          onChange(val);
                        }
                      }
                    }}
                    placeholder="#000000"
                    className={`flex-1 rounded-md px-2 py-1.5 font-mono text-sm ${
                      darkMode
                        ? 'border-gray-600 bg-gray-900 text-gray-300'
                        : 'border-gray-300 bg-gray-50 text-gray-900'
                    } border`}
                  />
                </div>
              </div>
            </div>
            <Popover.Arrow className={darkMode ? 'fill-gray-800' : 'fill-white'} />
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
}
